/** Alkalmazásváz: fejléc, fülek, tartalomjegyzék, státuszsor, bannerek. */
import { render, stats, loadHighlighter } from '../render/markdown';
import { renderMermaid } from '../render/mermaid';
import { isDark, toggleTheme, applyTheme } from './theme';
import type { DocState } from '../store/document';

export type ViewMode = 'read' | 'edit';

export interface ShellHooks {
  onViewChange(mode: ViewMode): void;
  onSave(): void;
  onRename(): void;
}

export class Shell {
  readonly editorHost: HTMLElement;
  private root: HTMLElement;
  private previewHost!: HTMLElement;
  private tocHost!: HTMLElement;
  private titleEl!: HTMLElement;
  private statusEl!: HTMLElement;
  private metaEl!: HTMLElement;
  private bannerHost!: HTMLElement;
  private tabs!: HTMLElement;
  private mode: ViewMode = 'read';
  private lastRendered = '';

  constructor(
    root: HTMLElement,
    private hooks: ShellHooks,
  ) {
    this.root = root;
    applyTheme();
    this.root.innerHTML = `
      <header class="topbar">
        <div class="topbar__brand" title="Bitsquad MD">
          <span class="topbar__logo" aria-hidden="true">◧</span>
          <span class="topbar__name">Bitsquad MD</span>
        </div>
        <button class="topbar__title" data-rename title="Átnevezés"></button>
        <div class="topbar__right">
          <span class="status" data-status></span>
          <button class="iconbtn" data-theme title="Világos / sötét téma">☾</button>
        </div>
      </header>
      <nav class="tabs" role="tablist">
        <button class="tab tab--active" role="tab" data-view="read">Olvasás</button>
        <button class="tab" role="tab" data-view="edit">Szerkesztés</button>
      </nav>
      <div class="banners" data-banners></div>
      <main class="workspace">
        <aside class="toc" data-toc hidden></aside>
        <section class="pane pane--read" data-preview></section>
        <section class="pane pane--edit" data-editor hidden></section>
      </main>
      <footer class="statusbar"><span data-meta></span></footer>
    `;

    this.previewHost = root.querySelector('[data-preview]')!;
    this.editorHost = root.querySelector('[data-editor]')!;
    this.tocHost = root.querySelector('[data-toc]')!;
    this.titleEl = root.querySelector('[data-rename]')!;
    this.statusEl = root.querySelector('[data-status]')!;
    this.metaEl = root.querySelector('[data-meta]')!;
    this.bannerHost = root.querySelector('[data-banners]')!;
    this.tabs = root.querySelector('.tabs')!;

    this.tabs.addEventListener('click', (e) => {
      const btn = (e.target as HTMLElement).closest<HTMLElement>('[data-view]');
      if (btn) this.setMode(btn.dataset.view as ViewMode);
    });
    root.querySelector('[data-theme]')!.addEventListener('click', () => {
      toggleTheme();
      this.rerenderPreview(true);
    });
    this.titleEl.addEventListener('click', () => this.hooks.onRename());

    window.addEventListener('keydown', (e) => {
      const mod = e.metaKey || e.ctrlKey;
      if (mod && e.key.toLowerCase() === 's') {
        e.preventDefault();
        this.hooks.onSave();
      }
      if (mod && e.key.toLowerCase() === 'e') {
        e.preventDefault();
        this.setMode(this.mode === 'read' ? 'edit' : 'read');
      }
    });
  }

  setMode(mode: ViewMode): void {
    if (mode === this.mode) return;
    this.mode = mode;
    for (const tab of this.tabs.querySelectorAll<HTMLElement>('[data-view]')) {
      tab.classList.toggle('tab--active', tab.dataset.view === mode);
    }
    this.previewHost.hidden = mode !== 'read';
    this.editorHost.hidden = mode !== 'edit';
    this.tocHost.hidden = mode !== 'read' || !this.tocHost.childElementCount;
    this.hooks.onViewChange(mode);
  }

  getMode(): ViewMode {
    return this.mode;
  }

  /** Teljes állapotfrissítés a store-ból. */
  update(state: DocState): void {
    this.titleEl.textContent = state.meta?.name ?? 'Névtelen dokumentum';
    document.title = state.meta ? `${state.meta.name} — Bitsquad MD` : 'Bitsquad MD';

    const { words, minutes } = stats(state.content);
    this.metaEl.textContent = `${words.toLocaleString('hu-HU')} szó · ${minutes} perc olvasás`;

    this.statusEl.className = `status status--${state.saveState}`;
    this.statusEl.textContent = statusText(state);

    const editTab = this.tabs.querySelector<HTMLButtonElement>('[data-view="edit"]')!;
    editTab.disabled = state.readOnly;
    editTab.title = state.readOnly ? 'Ehhez a fájlhoz nincs szerkesztési jogod' : '';
  }

  /** A renderelt nézet frissítése (csak ha változott a forrás). */
  async rerenderPreview(force = false, source?: string): Promise<void> {
    const text = source ?? this.lastRendered;
    if (!force && text === this.lastRendered && this.previewHost.childElementCount) return;
    this.lastRendered = text;

    await loadHighlighter();
    const result = render(text);
    this.previewHost.innerHTML = `
      ${result.frontMatter ? `<details class="frontmatter"><summary>Metaadatok</summary><pre></pre></details>` : ''}
      <article class="prose"></article>
    `;
    if (result.frontMatter) {
      this.previewHost.querySelector('.frontmatter pre')!.textContent = result.frontMatter;
    }
    this.previewHost.querySelector('.prose')!.innerHTML = result.html;

    this.buildToc(result.headings);
    this.addCopyButtons();
    if (result.hasMermaid) void renderMermaid(this.previewHost, isDark());
  }

  private buildToc(headings: { level: number; text: string; slug: string }[]): void {
    const useful = headings.filter((h) => h.level >= 2 && h.level <= 3);
    if (useful.length < 3) {
      this.tocHost.innerHTML = '';
      this.tocHost.hidden = true;
      return;
    }
    this.tocHost.innerHTML =
      '<p class="toc__title">Tartalom</p><ul class="toc__list">' +
      useful
        .map(
          (h) =>
            `<li class="toc__item toc__item--l${h.level}"><a href="#${h.slug}"></a></li>`,
        )
        .join('') +
      '</ul>';
    this.tocHost.querySelectorAll('a').forEach((a, i) => {
      a.textContent = useful[i].text;
    });
    this.tocHost.hidden = this.mode !== 'read';
  }

  private addCopyButtons(): void {
    for (const pre of this.previewHost.querySelectorAll<HTMLElement>('pre.code-block')) {
      const btn = document.createElement('button');
      btn.className = 'copybtn';
      btn.type = 'button';
      btn.textContent = 'Másolás';
      btn.addEventListener('click', async () => {
        await navigator.clipboard.writeText(pre.textContent ?? '');
        btn.textContent = 'Másolva';
        setTimeout(() => (btn.textContent = 'Másolás'), 1500);
      });
      pre.appendChild(btn);
    }
  }

  setCursor(line: number, col: number): void {
    this.metaEl.dataset.cursor = `Sor ${line}, oszlop ${col}`;
    this.metaEl.textContent = `${this.metaEl.textContent?.split(' · UTF-8')[0] ?? ''} · UTF-8 · Sor ${line}, oszlop ${col}`;
  }

  banner(
    id: string,
    text: string,
    variant: 'info' | 'warn' | 'error' = 'info',
    action?: { label: string; run: () => void },
  ): void {
    this.dismissBanner(id);
    const el = document.createElement('div');
    el.className = `banner banner--${variant}`;
    el.dataset.id = id;
    const span = document.createElement('span');
    span.textContent = text;
    el.appendChild(span);
    if (action) {
      const btn = document.createElement('button');
      btn.className = 'btn btn--ghost btn--sm';
      btn.textContent = action.label;
      btn.addEventListener('click', action.run);
      el.appendChild(btn);
    }
    this.bannerHost.appendChild(el);
  }

  dismissBanner(id: string): void {
    this.bannerHost.querySelector(`[data-id="${id}"]`)?.remove();
  }

  fatal(title: string, detail: string): void {
    this.root.innerHTML = `<div class="fatal"><h1></h1><p></p>
      <a class="btn btn--primary" href="https://drive.google.com" target="_blank" rel="noopener">Vissza a Drive-ba</a></div>`;
    this.root.querySelector('h1')!.textContent = title;
    this.root.querySelector('p')!.textContent = detail;
  }
}

function statusText(state: DocState): string {
  switch (state.saveState) {
    case 'saving':
      return 'Mentés…';
    case 'saved':
      return state.lastSavedAt
        ? `Mentve ${state.lastSavedAt.toLocaleTimeString('hu-HU', { hour: '2-digit', minute: '2-digit' })}`
        : 'Mentve';
    case 'dirty':
      return 'Nem mentett módosítások';
    case 'conflict':
      return 'Ütközés';
    case 'error':
      return state.error ?? 'Hiba';
    default:
      return state.readOnly ? 'Csak olvasható' : '';
  }
}
